import type { FolderEntry } from '@xphoto/shared';

/**
 * Helpers PURS de l'arbre de dossiers du sélecteur de destination (LOT 5).
 *
 * Le serveur (`GET /all-folders`) ne remonte QUE les dossiers qui contiennent
 * DIRECTEMENT des fichiers. Pour rendre N'IMPORTE quel niveau sélectionnable comme
 * destination (ex. `2024` alors que les fichiers vivent seulement sous
 * `2024/vacances`), on recompose ici l'ensemble complet des chemins de dossier :
 * chaque dossier + TOUS ses ancêtres + la racine (`''`).
 *
 * Ces fonctions sont pures (aucun accès réseau/DOM), triées de façon déterministe,
 * et testées isolément — pas d'`any`.
 */

/**
 * Un nœud de dossier prêt à afficher : son chemin POSIX (`''` = racine), sa
 * profondeur (nombre de segments ; racine = 0) et le nombre de fichiers DIRECTS
 * quand le serveur l'a remonté (`undefined` pour un ancêtre purement recomposé).
 */
export interface FolderNode {
  path: string;
  depth: number;
  /** Fichiers directs (issus du serveur) ou `undefined` si dossier ancêtre recomposé. */
  assetCount?: number;
}

/** Profondeur d'un chemin POSIX : nombre de segments (`''` → 0, `a/b` → 2). */
function depthOf(path: string): number {
  return path === '' ? 0 : path.split('/').length;
}

/**
 * Tous les préfixes ancêtres d'un chemin de dossier, RACINE incluse. Pour `a/b/c` :
 * `['', 'a', 'a/b', 'a/b/c']`. Pour `''` : `['']`.
 */
function ancestorsOf(path: string): string[] {
  const result = ['']; // la racine est ancêtre de tout
  if (path === '') return result;
  const segments = path.split('/');
  let acc = '';
  for (const seg of segments) {
    acc = acc === '' ? seg : `${acc}/${seg}`;
    result.push(acc);
  }
  return result;
}

/**
 * Construit la liste TRIÉE et DÉDOUBLONNÉE de tous les dossiers sélectionnables à
 * partir des entrées serveur : chaque dossier + ses ancêtres + la racine (`''`).
 *
 * - Le `assetCount` d'un dossier n'est porté que si le serveur l'a remonté (compte
 *   de fichiers DIRECTS) ; un ancêtre purement recomposé a `assetCount` non défini.
 * - Tri : par chemin, comparaison insensible à la casse et aux accents (via
 *   `localeCompare`), la racine (`''`) toujours en tête. Déterministe et stable
 *   (départage par le chemin brut).
 */
export function buildFolderTree(entries: readonly FolderEntry[]): FolderNode[] {
  // Compte direct par chemin exact remonté par le serveur.
  const directCount = new Map<string, number>();
  for (const e of entries) directCount.set(e.path, e.assetCount);

  // Ensemble complet des chemins : chaque dossier + ses ancêtres + racine.
  const paths = new Set<string>(['']);
  for (const e of entries) {
    for (const ancestor of ancestorsOf(e.path)) paths.add(ancestor);
  }

  const nodes: FolderNode[] = [...paths].map((path) => {
    const count = directCount.get(path);
    return count === undefined
      ? { path, depth: depthOf(path) }
      : { path, depth: depthOf(path), assetCount: count };
  });

  nodes.sort((a, b) => {
    if (a.path === b.path) return 0;
    if (a.path === '') return -1; // racine toujours en tête
    if (b.path === '') return 1;
    const cmp = a.path.localeCompare(b.path, undefined, { sensitivity: 'base' });
    return cmp !== 0 ? cmp : a.path < b.path ? -1 : 1;
  });
  return nodes;
}

/**
 * Filtre une liste de nœuds par sous-chaîne, insensible à la casse et aux accents.
 * Une requête vide renvoie la liste inchangée. La racine (`''`) ne matche que sur
 * une requête vide (aucun texte à chercher dedans).
 */
export function filterFolderNodes(nodes: readonly FolderNode[], query: string): FolderNode[] {
  const needle = normalize(query);
  if (needle === '') return [...nodes];
  return nodes.filter((n) => normalize(n.path).includes(needle));
}

/**
 * Compose le chemin d'un NOUVEAU dossier à partir du parent choisi (`''` = racine) et
 * du nom saisi. Renvoie `''` si le nom est vide (rien à créer). PUR. La normalisation
 * et la validation restent déléguées à `sanitizeDestFolder`. Partagé par les modales
 * de téléversement et de déplacement (carte « Nouveau dossier ici »).
 */
export function composeNewFolderPath(parent: string, name: string): string {
  const trimmed = name.trim();
  if (trimmed === '') return '';
  return parent === '' ? trimmed : `${parent}/${trimmed}`;
}

// ---------------------------------------------------------------------------
// Arbre REPLIABLE (rendu hiérarchique du sélecteur de dossier de l'upload)
// ---------------------------------------------------------------------------

/**
 * Chemin du dossier PARENT direct d'un chemin POSIX (`''` = racine). Pour `a/b/c` :
 * `a/b`. Pour un dossier de 1er niveau (`a`) ou la racine (`''`) : `''`.
 */
export function parentOf(path: string): string {
  const idx = path.lastIndexOf('/');
  return idx === -1 ? '' : path.slice(0, idx);
}

/**
 * Ancêtres PROPRES d'un chemin à DÉPLIER pour le rendre visible : tous les préfixes
 * SAUF la racine (`''`, toujours implicitement ouverte) ET le chemin lui-même. Pour
 * `a/b/c` : `['a', 'a/b']`. Pour un dossier de 1er niveau (`a`) ou la racine : `[]`.
 */
export function ancestorPaths(path: string): string[] {
  if (path === '') return [];
  const segments = path.split('/');
  const out: string[] = [];
  let acc = '';
  // Tous les préfixes SAUF le dernier segment (le chemin complet lui-même).
  for (let i = 0; i < segments.length - 1; i += 1) {
    const seg = segments[i];
    if (seg === undefined) continue;
    acc = acc === '' ? seg : `${acc}/${seg}`;
    out.push(acc);
  }
  return out;
}

/**
 * Ensemble des chemins qui ONT au moins un enfant direct dans `nodes` (les seuls
 * qui portent un chevron de pliage). La racine (`''`) n'y figure jamais (toujours
 * dépliée, sans chevron).
 */
export function collectParentPaths(nodes: readonly FolderNode[]): Set<string> {
  const parents = new Set<string>();
  for (const n of nodes) {
    if (n.path === '') continue;
    parents.add(parentOf(n.path));
  }
  parents.delete('');
  return parents;
}

/**
 * Un nœud est VISIBLE dans l'arbre replié si TOUS ses ancêtres propres sont dépliés
 * (dans `expanded`). La racine est implicitement ouverte : les dossiers de 1er niveau
 * (sans ancêtre propre) sont donc toujours visibles. Nœud racine (`''`) : toujours vrai.
 */
export function isNodeVisible(path: string, expanded: ReadonlySet<string>): boolean {
  return ancestorPaths(path).every((a) => expanded.has(a));
}

/** Normalise pour la recherche : minuscules, sans accents, trimé. */
function normalize(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}
